import { computed, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toastStore';
import type { LoadTask } from '@/composeables/loaders/LoadTask';
import { TaskLoaderState } from './TaskLoaderState';

export const useTaskLoader = (params?: { waitForFirstLoader?: boolean }) => {
    const state = ref(new TaskLoaderState());
    state.value.isActive = params?.waitForFirstLoader ?? false;

    const resetPeriod = 5000; // 5 seconds reset period

    // We have an internal timer that will reset the LoadState after 5 seconds of inactivity
    // The reason we do this is so f.eks buttons get their icon state back
    let resetTimer: number | null = null;

    const AddLoader = (loader: () => Promise<void>, key: string, icon: string | null = null, noToast: boolean = false) => {
        const prom = loader();
        const taskState: LoadTask = { prom: prom, key: key, icon: icon || '', done: false, error: null };
        state.value.NeedsLoading.push(taskState);
        state.value.isActive = true;

        if (resetTimer !== null) {
            window.clearTimeout(resetTimer);
            resetTimer = null;
        }

        prom.then(() => {
            state.value.Loaded.push(prom);
        })
            .catch((e: any) => {
                if (process.env.NODE_ENV == 'development') {
                    console.error(e);
                }
                taskState.error = e.message;
                state.value.Failed.push(taskState);

                if ((e as Error).cause != '409') {
                    // 409 messages we handle differently
                    if (!noToast) {
                        useToastStore().add({ level: 'ERROR', message: e.message });
                    }
                }
            })
            .finally(() => {
                state.value.isActive = state.value.NeedsLoading.length > 0 && !state.value.AllDone;
                state.value.NeedsLoading.find((x) => !x.done && x.key == key)!.done = true;
            });

        return prom;
    };

    // Is Set when all loaders are done
    const AllDone = computed(() => {
        return state.value.NeedsLoading.length == state.value.Loaded.length + state.value.Failed.length;
    });

    /**
     * Clears all loaders
     */
    const ClearLoaders = () => {
        state.value.NeedsLoading = [];
        state.value.Loaded = [];
        state.value.Failed = [];
        state.value.isActive = false;
    };

    watch(AllDone, (val) => {
        state.value.AllDone = val;

        if (val) {
            if (resetTimer !== null) {
                window.clearTimeout(resetTimer);
                resetTimer = null;
            }

            resetTimer = window.setTimeout(() => {
                ClearLoaders();
                resetTimer = null;
            }, resetPeriod);
        }
    });

    return { AddLoader, ClearLoaders, AllDone, LoadState: state };
};
